import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, Zap, BookOpen, Trophy, ArrowRight, MessageCircle, Plus, CircleCheck, CircleX, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Progress } from "@/components/ui/progress";

const Stat = ({ icon: Icon, label, value, tint, testId }) => (
  <div className="rounded-3xl bg-white border border-slate-200 p-5">
    <span className={`h-10 w-10 rounded-xl grid place-items-center ${tint}`}><Icon size={20} /></span>
    <p data-testid={testId} className="font-display text-3xl font-extrabold text-slate-900 mt-4">{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

export default function Dashboard() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => {
      setD(data);
      setUser(data.user);
      if (!data.languages.length) navigate("/languages");
    });
  }, [navigate, setUser]);

  if (!d) return null;
  const current = d.languages.find((l) => l.language === d.user.current_language) || d.languages[0];
  if (!current) return null;
  const n = current.next;
  const continueTo = n ? (n.topic_id === "final" ? `/assessment/${current.language}/${n.level}/final` : `/lesson/${current.language}/${n.level}/${n.topic_id}`) : `/learn/${current.language}`;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <p className="caption">Dashboard</p>
      <h1 data-testid="dashboard-greeting" className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1">
        {d.user.active_today ? "Great work today" : "Welcome back"}, {d.user.name.split(" ")[0]}.
      </h1>

      <div className="grid lg:grid-cols-12 gap-6 mt-8">
        <div data-testid="continue-card" className="lg:col-span-7 rounded-[2rem] bg-[hsl(var(--primary))] text-white p-7 sm:p-8 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
          <div className="absolute right-16 -bottom-16 h-40 w-40 rounded-full bg-white/10" />
          <p className="text-white/80 text-sm font-semibold uppercase tracking-wider">{current.name} · {current.current_level}</p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-2">{n ? n.title : "Everything complete!"}</h2>
          <p className="text-white/85 mt-1 text-sm">{n ? "Pick up right where you left off." : "You've finished every topic. Keep practising with your tutor."}</p>
          <div className="mt-6 max-w-sm">
            <Progress value={(current.level_completed / current.level_total) * 100} className="h-2.5 bg-white/25 [&>div]:bg-white" />
            <p className="text-xs text-white/80 mt-2">{current.level_completed}/{current.level_total} topics in this level</p>
          </div>
          <div className="flex flex-wrap gap-2 mt-7 relative">
            <button data-testid="dashboard-continue-button" onClick={() => navigate(continueTo)} className="inline-flex items-center gap-2 rounded-full bg-white text-[hsl(var(--primary-dark))] font-bold px-6 py-3 hover:scale-[1.02] active:scale-95 transition-transform">
              Continue <ArrowRight size={18} />
            </button>
            <button data-testid="dashboard-path-button" onClick={() => navigate(`/learn/${current.language}`)} className="rounded-full border-2 border-white/50 font-bold px-5 py-3 hover:bg-white/10">View path</button>
            <button data-testid="dashboard-tutor-button" onClick={() => navigate(`/tutor/${current.language}`)} className="rounded-full border-2 border-white/50 font-bold px-5 py-3 hover:bg-white/10 inline-flex items-center gap-2"><MessageCircle size={17} /> Tutor</button>
          </div>
        </div>
        <div className="lg:col-span-5 grid grid-cols-2 gap-4">
          <Stat icon={Flame} label="Day streak" value={d.user.streak} tint="bg-orange-50 text-orange-500" testId="stat-streak" />
          <Stat icon={Zap} label="Total XP" value={d.user.xp} tint="bg-amber-50 text-amber-500" testId="stat-xp" />
          <Stat icon={BookOpen} label="Topics completed" value={d.topics_completed} tint="bg-emerald-50 text-emerald-600" testId="stat-topics" />
          <Stat icon={Trophy} label="Best streak" value={d.user.best_streak} tint="bg-blue-50 text-blue-500" testId="stat-best-streak" />
        </div>
      </div>

      <button data-testid="dashboard-review-card" onClick={() => navigate(`/review/${current.language}`)} className="mt-6 w-full text-left rounded-3xl bg-[hsl(var(--accent-blue-light))] border-2 border-[hsl(var(--accent-blue))]/25 p-5 flex items-center gap-4 hover:-translate-y-0.5 transition-transform">
        <span className="h-12 w-12 rounded-2xl bg-white grid place-items-center"><RotateCcw className="text-[hsl(var(--accent-blue))]" /></span>
        <span className="flex-1">
          <span data-testid="dashboard-review-due" className="block font-display font-bold text-lg text-slate-900">
            {current.review_due ? `${current.review_due} mistake${current.review_due > 1 ? "s" : ""} to review today` : "Review deck is clear"}
          </span>
          <span className="block text-sm text-slate-600">Daily deck of words you missed in {current.name}. Two right answers and they're gone.</span>
        </span>
        <ArrowRight className="text-[hsl(var(--accent-blue))]" />
      </button>

      <div className="grid lg:grid-cols-12 gap-6 mt-10">
        <section className="lg:col-span-7">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-slate-900">My languages</h2>
            <Link to="/languages" data-testid="add-language-link" className="text-sm font-bold text-[hsl(var(--primary-dark))] flex items-center gap-1"><Plus size={16} /> Add language</Link>
          </div>
          <div className="space-y-3">
            {d.languages.map((l) => (
              <button key={l.language} data-testid={`dashboard-language-${l.language}`} onClick={() => navigate(`/learn/${l.language}`)} className="w-full text-left rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow]">
                <span className="font-display text-lg font-extrabold text-slate-900 w-24">{l.name}</span>
                <div className="flex-1">
                  <div className="flex justify-between text-xs text-slate-500 mb-1.5"><span className="capitalize font-semibold">{l.current_level}</span><span>{l.level_completed}/{l.level_total}</span></div>
                  <Progress value={(l.level_completed / l.level_total) * 100} className="h-2" />
                </div>
                <ArrowRight size={18} className="text-slate-400" />
              </button>
            ))}
          </div>
        </section>
        <section className="lg:col-span-5">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Recent assessments</h2>
          {d.recent.length === 0 ? (
            <p data-testid="recent-empty" className="text-sm text-slate-500 rounded-2xl border border-dashed border-slate-300 p-6 text-center">No assessments yet. Finish a lesson and test yourself!</p>
          ) : (
            <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
              {d.recent.map((r) => (
                <div key={r.id} data-testid={`recent-assessment-${r.id}`} className="p-4 flex items-center gap-3">
                  {r.passed ? <CircleCheck className="text-emerald-600" size={20} /> : <CircleX className="text-rose-500" size={20} />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{r.topic_title}</p>
                    <p className="text-xs text-slate-500 uppercase">{r.language} · {r.level}</p>
                  </div>
                  <span className={`font-display font-extrabold ${r.passed ? "text-emerald-600" : "text-rose-500"}`}>{r.score}%</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
